import { EntityId } from "@jackfruit/common"
import { SagaIterator } from "@redux-saga/types"
import { nanoid, PayloadAction } from "@reduxjs/toolkit"
import { call, put, takeEvery } from "redux-saga/effects"
import { DocumentAnalysis } from "~/interfaces/entities/DocumentAnalysis"
import { DocumentConversion } from "~/interfaces/entities/DocumentConversion"
import { UploadEntity } from "~/interfaces/entities/Upload"
import { getToaster, getTranslator } from "~/redux/context"
import { getDocumentApi } from "~/redux/saga/documentApi"
import { getUpload } from "~/redux/saga/uploads"
import { lineItems } from "~/redux/state/lineItems"
import { DocumentApi } from "~/services/DocumentApi"
import { Files } from "~/services/Files"
import { actions, UpdateDocumentPayload } from "../process"
import { uploads } from "../state/uploads"
import { processUploadToS3Sync } from "./images"

export default function* watchUpdateDocument() {
  yield takeEvery(actions.updateDocument.type, processUpdateDocument)
}

export function* processUpdateDocument(
  action: PayloadAction<UpdateDocumentPayload>
): SagaIterator<void> {
  const { lineItemId, file } = action.payload
  const { updateOne: updateOneLineItem } = lineItems.actions

  yield put(
    updateOneLineItem({
      id: lineItemId,
      changes: {
        isProcessing: true,
      },
    })
  )

  try {
    const {
      documentAnalysis,
      uploadId,
    }: {
      documentAnalysis: DocumentAnalysis
      uploadId: EntityId
    } = yield call(() => createUploadFromDocumentFile({ file }))

    yield put(
      updateOneLineItem({
        id: lineItemId,
        changes: {
          documentUploadId: uploadId,
          documentPageCount: documentAnalysis.pageCount,
          isProcessing: false,
        },
      })
    )
  } catch (error) {
    yield put(
      updateOneLineItem({
        id: lineItemId,
        changes: {
          isProcessing: false,
        },
      })
    )
    const toaster = getToaster()
    const t = getTranslator()

    toaster({
      title: t("redux.saga.processUpdateDocument.error.updateDocument.title"),
      description: t(
        "redux.saga.processUpdateDocument.error.updateDocument.description"
      ),
      status: "error",
      duration: 9000,
      isClosable: true,
    })
  }
}

export function* createUploadFromDocumentFile({
  file,
}: {
  file: File
}): SagaIterator<{
  uploadId: EntityId
  documentAnalysis: DocumentAnalysis
}> {
  const { addOne: addOneUpload, updateOne: updateOneUpload } = uploads.actions

  const uploadId = nanoid()

  yield put(
    addOneUpload({
      id: uploadId,
      name: "",
      type: "",
      size: 0,
      imageId: "",
      status: "queued",
      isReady: false,
      hash: "",
      progress: 0,
      hasFailed: false,
      error: null,
      bucket: "",
      key: "",
      location: "",
      path: "",
      localUrl: "",
    })
  )

  yield call(processUploadToS3Sync, {
    file,
    id: uploadId,
  })

  const upload: UploadEntity = yield call(getUpload, uploadId)
  const documentApi: DocumentApi = yield call(getDocumentApi)

  const documentConversionResult: DocumentConversion = yield call(() =>
    documentApi.processDocumentConversion({
      url: upload.location,
    })
  )

  const documentLocation = documentConversionResult.url
  const documentPath = documentConversionResult.presignedUrl

  const preDocumentAnalysisResult: DocumentAnalysis = yield call(() =>
    documentApi.processDocumentAnalysis({ url: documentLocation })
  )

  if (!preDocumentAnalysisResult.valid) {
    if (preDocumentAnalysisResult.encrypted) {
      throw new Error("Document is encrypted and cannot be processed.")
    } else {
      throw new Error("Document is invalid and cannot be processed.")
    }
  }

  const localDocumentFile = yield call(Files.fetchDocumentFromUrl, documentPath)

  yield put(
    updateOneUpload({
      id: uploadId,
      changes: {
        location: documentLocation,
        localUrl: window.URL.createObjectURL(localDocumentFile),
        path: documentPath,
        // ensure the file has a .pdf extension and type
        name: upload.name.replace(/\.[^/.]+$/, "") + ".pdf",
        type: "application/pdf",
      },
    })
  )

  const documentAnalysisResult: DocumentAnalysis = yield call(() =>
    documentApi.processDocumentAnalysis({ url: documentLocation })
  )

  return {
    uploadId,
    documentAnalysis: documentAnalysisResult,
  }
}
